import imageCompression from 'browser-image-compression';

/**
 * Compress an image file to a maximum size of 200KB
 * @param {File} imageFile - The image file to compress
 * @param {Object} options - Optional compression options
 * @returns {Promise<File>} - The compressed image file
 */
export async function compressImage(imageFile, options = {}) {
  const defaultOptions = {
    maxSizeMB: 0.2, // 200KB max
    maxWidthOrHeight: 1920, // Max dimension
    useWebWorker: true,
    fileType: 'image/jpeg', // Convert to JPEG for better compression
    initialQuality: 0.8,
    ...options
  };

  try {
    // Check if compression is needed
    const fileSizeKB = imageFile.size / 1024;
    
    if (fileSizeKB <= 200) {
      console.log(`Image already under 200KB (${fileSizeKB.toFixed(1)}KB), skipping compression`);
      return imageFile;
    }

    console.log(`Compressing image from ${fileSizeKB.toFixed(1)}KB...`);
    
    const compressedFile = await imageCompression(imageFile, defaultOptions);
    
    const compressedSizeKB = compressedFile.size / 1024;
    const savings = ((1 - compressedFile.size / imageFile.size) * 100).toFixed(1);
    
    console.log(`Compressed to ${compressedSizeKB.toFixed(1)}KB (${savings}% reduction)`);
    
    return compressedFile;
  } catch (error) {
    console.error('Image compression failed:', error);
    // Return original file if compression fails
    return imageFile;
  }
}

/**
 * Compress an image and convert to Base64
 * @param {File} imageFile - The image file to compress
 * @param {Object} options - Optional compression options
 * @returns {Promise<string>} - Base64 encoded compressed image
 */
export async function compressImageToBase64(imageFile, options = {}) {
  const compressedFile = await compressImage(imageFile, options);
  
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(compressedFile);
  });
}

/**
 * Compress image for profile pictures (smaller, square-optimized)
 */
export async function compressProfilePicture(imageFile) {
  return compressImage(imageFile, {
    maxSizeMB: 0.1, // 100KB max for profile pics
    maxWidthOrHeight: 500,
    useWebWorker: true,
    fileType: 'image/jpeg',
    initialQuality: 0.8
  });
}

/**
 * Compress image for banners (wider, larger allowed)
 */
export async function compressBannerImage(imageFile) {
  return compressImage(imageFile, {
    maxSizeMB: 0.2, // 200KB max for banners
    maxWidthOrHeight: 1200,
    useWebWorker: true,
    fileType: 'image/jpeg',
    initialQuality: 0.85
  });
}

/**
 * Compress image for community posts
 */
export async function compressPostImage(imageFile) {
  return compressImage(imageFile, {
    maxSizeMB: 0.2, // 200KB max
    maxWidthOrHeight: 1080,
    useWebWorker: true,
    fileType: 'image/jpeg',
    initialQuality: 0.8
  });
}

export default compressImage;
